import { useEffect, useState } from 'react';

import { api } from '../lib/api';
import { Card, Empty } from './ui';

interface EnvEntry {
  key: string;
  /** Null for a secret — the server never sends the value. */
  value: string | null;
  isSecret: boolean;
  hasValue: boolean;
}

interface EnvData {
  deployment: { id: string; name: string; product: string; client: string; server: string };
  /** Where the file REALLY is — resolved through any symlink, not the stack's own path. */
  path: string;
  /** How many stacks share this exact file. 1 means it belongs to this one alone. */
  sharedBy: number;
  /** The other stacks it belongs to, named. */
  sharedWith: string[];
  entries: EnvEntry[];
}

/**
 * Edits one deployment's .env.
 *
 * Secret values are never fetched. A secret shows as "set" or "not set" and an
 * empty box; typing in it replaces the value, leaving it blank leaves the value
 * untouched. You can rotate an API key without ever being shown the old one,
 * which is the only thing editing actually needs — and it keeps live credentials
 * out of the response, the browser tab and any proxy log in between.
 *
 * Only changed fields are sent, so an untouched secret is absent from the request
 * entirely rather than being round-tripped.
 */
export function EnvEditor({ deploymentId, label }: { deploymentId: string; label: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<EnvData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api<EnvData>(`/deployments/${deploymentId}/env`)
      .then((d) => {
        setData(d);
        setEdits({});
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read the file'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && !data && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pending = Object.keys(edits).filter((k) => edits[k] !== undefined);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const changes = { ...edits };
      if (newKey.trim()) changes[newKey.trim()] = newValue;

      const res = await api<{ changed: string[]; path: string; sharedBy: number }>(
        `/deployments/${deploymentId}/env`,
        { method: 'PATCH', body: JSON.stringify({ changes, removals: [] }) },
      );
      setSaved(`Wrote ${res.changed.join(", ")} to ${res.path}${res.sharedBy > 1 ? ` — shared by ${res.sharedBy} deployments` : ""}.`);
      setNewKey('');
      setNewValue('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Card
        title="Environment"
        actions={
          <button type="button" className="ghost" onClick={() => setOpen(true)}>
            Open .env
          </button>
        }
      >
        {/* No path here. It used to read `/opt/{label}/.env`, built by hand in
            the browser — which was wrong the moment stacks moved to
            /opt/<client>/<stack>, and wrong anyway for the five ABC stacks
            whose .env is a symlink to a shared file. The server knows the real
            path and reports it on open; the UI should not guess at one. */}
        <p className="muted small">
          Edit the environment variables for <strong>{label}</strong>. The file&apos;s real
          location is shown when it opens. Secret values are never displayed — you can replace
          one without seeing it.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title={`Environment · ${data?.path ?? label}`}
      actions={
        <button type="button" className="ghost" onClick={() => setOpen(false)}>
          Close
        </button>
      }
    >
      {loading && <p className="muted">Reading the file over SSH…</p>}

      {error && (
        <p className="notice notice-bad" role="alert">
          {error}
        </p>
      )}

      {/* Shown BEFORE anything is typed, not after saving. Five ABC stacks
          symlink their .env to one shared file, so a change here is a change to
          five applications — and the card title alone ("Environment ·
          /opt/abc/abc.env") does not say so loudly enough to stop someone. */}
      {data && data.sharedBy > 1 && (
        <p className="notice notice-bad">
          <strong>This file is shared by {data.sharedBy} deployments.</strong> Saving changes{' '}
          {data.sharedWith.length > 0 ? (
            <>
              <strong>{data.deployment.name}</strong> and {data.sharedWith.join(', ')}
            </>
          ) : (
            <>every deployment that symlinks to it</>
          )}
          , not just this one. Each still needs its own <em>Recreate</em> to pick the change up.
        </p>
      )}

      {data && (
        <>
          {/* Stated where the decision is made, not buried in a tooltip. Saving
              does not restart anything: .env is read when a container is created,
              so a change is inert until someone brings the stack up again. */}
          <p className="notice">
            Changes are written to the file but <strong>do not take effect until the containers are
            recreated</strong> — <code>docker compose up -d</code> in <code>/opt/{data.deployment.name}</code>.
            A timestamped backup is kept on the host each time.
          </p>

          <div className="env-grid">
            {data.entries.map((e) => (
              <label key={e.key} className="env-row">
                <span className="env-key mono">
                  {e.key}
                  {e.isSecret && (
                    <span className="tag" title="Treated as a secret: the value is never sent to the browser">
                      secret
                    </span>
                  )}
                </span>
                <input
                  type="text"
                  className="mono"
                  value={edits[e.key] ?? (e.isSecret ? '' : (e.value ?? ''))}
                  placeholder={e.isSecret ? (e.hasValue ? '•••••••• set — type to replace' : 'not set') : ''}
                  onChange={(ev) => setEdits((p) => ({ ...p, [e.key]: ev.target.value }))}
                />
              </label>
            ))}
          </div>

          {data.entries.length === 0 && <Empty>No variables in this file.</Empty>}

          <div className="env-add">
            <span className="muted small">Add a variable</span>
            <input
              type="text"
              className="mono"
              placeholder="NEW_KEY"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value.toUpperCase())}
            />
            <input
              type="text"
              className="mono"
              placeholder="value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
          </div>

          {saved && <p className="notice">{saved}</p>}

          <div className="cost-actions">
            <button
              type="button"
              disabled={saving || (pending.length === 0 && !newKey.trim())}
              onClick={() => void save()}
            >
              {saving ? 'Writing…' : `Save${pending.length ? ` (${pending.length})` : ''}`}
            </button>
            <button type="button" className="ghost" disabled={saving} onClick={load}>
              Reload
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Picks which stack's .env to edit on a server that has several.
 *
 * A card per deployment would put ten collapsed cards on the ABC host — an
 * accordion of near-identical headers that hides the one you want. One picker and
 * one editor keeps the page readable and makes the choice explicit, which for a
 * control that writes to production is the right emphasis.
 */
interface EnvFile {
  path: string;
  ownerMode: string;
  bytes: number;
  usedBy: string[];
  stacks: Array<{ id: string; name: string; product: string }>;
}

/**
 * The .env files on a server, one entry per real file.
 *
 * Listed by FILE rather than by deployment. Five ABC stacks symlink to a single
 * /opt/abc/abc.env, so a per-deployment list showed the same file five times
 * under five names — and editing any one of them changed all five
 * applications without saying so.
 *
 * Each entry names the stacks that share it, and offers a recreate per stack,
 * because one edit to a shared file needs every one of them restarted.
 */
export function EnvSection({ serverId, canEdit }: { serverId: string; canEdit: boolean }) {
  const [data, setData] = useState<{ files: EnvFile[]; problems: Array<{ stack: string; issue: string }> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    api<{ files: EnvFile[]; problems: Array<{ stack: string; issue: string }> }>(
      `/servers/${serverId}/env-files`,
    )
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not list .env files'));
  }, [serverId, canEdit]);

  if (!canEdit) return null;

  const chosen = data?.files.find((f) => f.path === selected) ?? null;

  return (
    <Card title="Environment files">
      <p className="muted small">
        One entry per file. Secret values are never displayed — you can replace one without being
        shown it.
      </p>

      {error && <p className="notice notice-bad" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Reading over SSH…</p>}
      {data?.files.length === 0 && <p className="muted small">No .env files on this host.</p>}

      <ul className="echip-row">
        {(data?.files ?? [])
          .slice()
          .sort((a, b) => a.path.localeCompare(b.path))
          .map((f) => (
            <li key={f.path}>
              <button
                type="button"
                className={selected === f.path ? '' : 'ghost'}
                onClick={() => setSelected(selected === f.path ? null : f.path)}
                title={`${f.path} · ${f.ownerMode} · ${f.bytes} bytes`}
              >
                <code>{f.path.replace(/^\/opt\//, '')}</code>
                {f.stacks.length > 1 && (
                  <span className="tag tag-warn">shared by {f.stacks.length}</span>
                )}
              </button>
            </li>
          ))}
      </ul>

      {/* Stacks with no .env, or a dangling symlink. Both are states worth
          seeing: the second is what happened when stacks moved to /opt without
          the shared file following them. */}
      {(data?.problems.length ?? 0) > 0 && (
        <p className="muted small">
          {data!.problems.map((p) => `${p.stack} — ${p.issue}`).join(' · ')}
        </p>
      )}

      {chosen && chosen.stacks.length > 0 && (
        <>
          <EnvEditor
            key={chosen.path}
            deploymentId={chosen.stacks[0].id}
            label={chosen.path.replace(/^\/opt\//, '')}
          />
          {/* The explanation is rendered ONCE here, not inside each control.
              It used to live in ComposeControls, which is rendered per
              deployment sharing the file — so the five ABC deployments on
              abc.env produced five identical copies of this heading and
              paragraph stacked down the page.

              The per-deployment buttons still repeat, and must: an edit to a
              shared .env is inert in each application until that one is
              recreated, so a single button would quietly leave four of them on
              the old value. */}
          <div className="compose-section">
            <h3>
              {chosen.stacks.length === 1
                ? 'Restart this application'
                : `Restart the ${chosen.stacks.length} applications sharing this file`}
            </h3>
            <p className="muted small">
              <strong>Restart</strong> bounces the containers and keeps their current environment.{' '}
              <strong>Recreate</strong> runs <code>docker compose up -d</code>, which is the one
              that applies an <code>.env</code> change. Either takes the app offline for a few
              seconds.
            </p>
            {chosen.stacks.length > 1 && (
              <p className="notice">
                Recreating one of these does <strong>not</strong> apply the change to the others —
                each reads the file when its own containers are created, so each needs recreating
                in turn.
              </p>
            )}
            {chosen.stacks.map((s) => (
              <ComposeControls key={`c-${s.id}`} deploymentId={s.id} label={`${s.product} · ${s.name}`} />
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Restart or recreate a stack.
 *
 * Two buttons rather than one, because the difference is the thing people get
 * wrong: `restart` bounces the containers and keeps their existing environment,
 * `up -d` recreates them and is the only one that picks up an .env edit.
 *
 * Both are behind a confirmation. This takes a client's production application
 * offline for a few seconds, and a button that does that on a single click sits
 * one stray tap away from an incident.
 */
function ComposeControls({ deploymentId, label }: { deploymentId: string; label: string }) {
  const [pending, setPending] = useState<'restart' | 'up' | null>(null);
  const [busy, setBusy] = useState<'restart' | 'up' | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: 'restart' | 'up') => {
    setBusy(action);
    setPending(null);
    setError(null);
    setResult(null);
    try {
      const res = await api<{ action: string; stack: string; server: string; output: string }>(
        `/deployments/${deploymentId}/compose`,
        { method: 'POST', body: JSON.stringify({ action }) },
      );
      setResult(
        `docker compose ${res.action} finished on ${res.server}.` +
          (res.output ? `\n\n${res.output}` : ''),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The command failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="compose-controls">
      {/* Which application these buttons act on. Named in the row rather than
          only in the confirmation, because with several of these stacked up the
          buttons are otherwise indistinguishable from each other. */}
      <code className="stack-name">{label}</code>

      {pending ? (
        <div className="notice notice-bad">
          <p>
            {pending === 'up' ? 'Recreate' : 'Restart'} <strong>{label}</strong>? This briefly takes
            it offline for whoever is using it.
          </p>
          <div className="cost-actions">
            <button type="button" onClick={() => void run(pending)}>
              Yes, {pending === 'up' ? 'recreate' : 'restart'} it
            </button>
            <button type="button" className="ghost" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="cost-actions">
          <button type="button" className="ghost" disabled={busy !== null}
            onClick={() => setPending('restart')}>
            {busy === 'restart' ? 'Restarting…' : 'Restart'}
          </button>
          <button type="button" className="ghost" disabled={busy !== null}
            onClick={() => setPending('up')}>
            {busy === 'up' ? 'Recreating…' : 'Recreate (apply .env)'}
          </button>
        </div>
      )}

      {busy && <p className="muted small">This can take a minute if an image needs pulling.</p>}
      {error && <p className="notice notice-bad" role="alert">{error}</p>}
      {result && <pre className="compose-output">{result}</pre>}
    </div>
  );
}
